/**
 * userTableWidthsApi — per-user column widths persisted via backend.
 *
 * Single GET on app load returns all widths keyed by table.
 * PUT per table_key writes one table's widths (avoids cross-table races).
 */
import { apiSlice } from './apiSlice'

export type ColumnWidths = Record<string, number>
export type TableWidthsDoc = Record<string, ColumnWidths>

export interface TableWidthsResponse {
  widths: TableWidthsDoc
}

export interface TableWidthsPutPayload {
  tableKey: string
  widths: ColumnWidths
}

const userTableWidthsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserTableWidths: builder.query<TableWidthsDoc, void>({
      query: () => ({ url: '/user-preferences/table-widths' }),
      transformResponse: (res: TableWidthsResponse) => res?.widths ?? {},
      providesTags: ['UserTableWidths'],
    }),

    putUserTableWidths: builder.mutation<{ message: string }, TableWidthsPutPayload>({
      query: ({ tableKey, widths }) => ({
        url: `/user-preferences/table-widths/${encodeURIComponent(tableKey)}`,
        method: 'PUT',
        body: { widths },
      }),
      onQueryStarted: async ({ tableKey, widths }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userTableWidthsApi.util.updateQueryData('getUserTableWidths', undefined, (draft) => {
            draft[tableKey] = { ...widths }
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    deleteUserTableWidths: builder.mutation<{ message: string }, string>({
      query: (tableKey) => ({
        url: `/user-preferences/table-widths/${encodeURIComponent(tableKey)}`,
        method: 'DELETE',
      }),
      onQueryStarted: async (tableKey, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          userTableWidthsApi.util.updateQueryData('getUserTableWidths', undefined, (draft) => {
            delete draft[tableKey]
          }),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),
  }),
})

export const {
  useGetUserTableWidthsQuery,
  usePutUserTableWidthsMutation,
  useDeleteUserTableWidthsMutation,
} = userTableWidthsApi

export default userTableWidthsApi
